import type { VFC } from 'react'
import type { DeferredPromise, IndexedArray } from './stateTypes'

export * from './loadingMask'
export * from './jwt'

/**
 * @deprecated
 */
export const loadJs = (url: string): Promise<Event> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    // script.charset = 'utf-8'
    // script.timeout = 120
    script.type = 'text/javascript'
    script.onload = resolve
    script.onerror = reject
    script.src = url
    const container = document.readyState === 'loading' ? document.head : document.body
    container.appendChild(script)
  })
}

/**
 * @deprecated
 */
export const loadCss = (url: string): Promise<Event> => {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link')
    link.type = 'text/css'
    link.rel = 'stylesheet'
    link.onload = resolve
    link.onerror = reject
    link.href = url
    document.head.appendChild(link)
  })
}

/**
 * Check a given value is an array with lengh > 0
 */
export const isNonemptyArray = <T = any>(arr: Array<T> | null | undefined | any): arr is NonNullable<Array<T>> => Array.isArray(arr) && arr.length > 0

/**
 * Ensure return an array by given thing.
 * It return directly if the given thing already an array,
 * while if it is null/undefined, it return empty array,
 * if it is a value, it will return a single item array with the value.
 */
export const ensureArray = <T = any>(thing: T | T[] | null | undefined): T[] => {
  if (Array.isArray(thing)) {
    return thing
  }
  if (thing == null) {
    return []
  }
  return [thing]
}

export interface IndexArray<T> extends IndexedArray<T> {
  _map: Map<any, any>,
  reIndex(): IndexArray<T>,
}

export const indexArray = <T = any>(arr: Array<T>, key: string | ((item: T, array: T[]) => string), options: { clone?: boolean, cached?: boolean } = {}): IndexArray<T> => {
  const { clone, cached = true } = options
  if (cached && Array.isArray(arr) && ('_map' in arr) && ('get' in arr)) {
    return arr as IndexArray<any>
  }
  const array = Array.isArray(arr) ? arr : []
  const reIndex = () => indexArray(array, key, { clone: false, cached: false })
  if (!isNonemptyArray(array)) {
    return Object.defineProperties(array, {
      _map: { configurable: true, value: new Map() },
      has: { configurable: true, value: () => false },
      get: { configurable: true, value: () => undefined },
      reIndex: { configurable: true, value: reIndex },
    }) as IndexArray<any>
  }
  const eachFn = isFunction(key) ? (a: T) => {
    const idx = key(a, array)
    if (idx === undefined) {
      return
    }
    return [idx, a]
  } : (a: T) => {
    const idx = (a as any)?.[key]
    if (idx === undefined) {
      return
    }
    return [idx, a]
  }
  const map = new Map<string, T>(array.map(eachFn).filter(Boolean) as [string, T][])
  return Object.defineProperties(clone ? [...array] : array, {
    _map: { configurable: true, value: map },
    has: { configurable: true, value: (id: any) => map.has(id) },
    get: { configurable: true, value: (id: any) => map.get(id) },
    reIndex: { configurable: true, value: reIndex },
  }) as IndexArray<T>
}

export const throwErrorFn = (err: any) => {
  throw err
}

export const tryParseErrorJson = (text: string | undefined) => {
  if (text && /^[{["]/.test(text.trim())) {
    try {
      return JSON.parse(text)
    }
    catch (e) {
      return text
    }
  }
  return text
}

export type DedupPromiseOptions = {
  keepPromiseIn?: number
}
export type DedupPromiseFn = <T = any>(uniqueKey: string, fn: () => Promise<T>, options?: DedupPromiseOptions) => Promise<T>
export type DedupPromise = DedupPromiseFn & {
  map: Map<string, Promise<any>>
  clear: () => void
  remove: (key: string) => void
}
const dedupPromiseMap = new Map<string, Promise<any>>()
const dedupPromiseFn: DedupPromiseFn = (uniqueKey, fn, options) => {
  if (!uniqueKey) {
    return Promise.reject(new Error('key is required'))
  }
  if (typeof fn !== 'function') {
    return Promise.reject(new Error('fn must be a function'))
  }
  const cachedPromise = dedupPromiseMap.get(uniqueKey)
  if (cachedPromise) {
    return cachedPromise
  }
  const promise = fn()
  dedupPromiseMap.set(uniqueKey, promise)
  Promise.resolve(promise).then(() => {
    const keepPromiseIn = options?.keepPromiseIn || 0
    if (!keepPromiseIn) {
      dedupPromiseMap.delete(uniqueKey)
    }
    else if (keepPromiseIn > 0 && Number.isFinite(keepPromiseIn)) {
      setTimeout(() => {
        dedupPromiseMap.delete(uniqueKey)
      }, keepPromiseIn)
    }
    // else keep promise forever
  }, () => {
    dedupPromiseMap.delete(uniqueKey) // always immediately remove promise on error
  })
  return promise
}
export const dedupPromise = Object.defineProperties(dedupPromiseFn, {
  map: { value: dedupPromiseMap },
  clear: { value: () => dedupPromiseMap.clear() },
  remove: { value: (key: string) => dedupPromiseMap.delete(key) },
}) as DedupPromise

export const createDeferredPromise = <T>(): DeferredPromise<T> => {
  const deferred = {} as any
  const promise = new Promise((res, rej) => {
    deferred.resolve = (result: any) => {
      promise.result = result
      res(result)
    }
    deferred.reject = (error: any) => {
      promise.error = error
      rej(error)
    }
  }) as DeferredPromise<T>
  return Object.assign(promise, deferred)
}

const getDefaultTimeoutMessage = (timeout: number) => {
  if (timeout % 1000) {
    return `${timeout}ms timeout`
  }
  return `${timeout / 1000}s timeout`
}
export const createTimeoutPromise = (timeout: number, errorMsg?: string): [Promise<never>, () => void] => {
  let ts: any
  const timeoutPromise = new Promise((_, reject) => {
    ts = setTimeout(() => {
      ts = undefined
      const err = new Error(errorMsg || getDefaultTimeoutMessage(timeout))
      err.name = 'TimeoutError'
      Object.defineProperties(err, {
        timeout: { value: timeout },
      })
      reject(err)
    }, timeout)
  }) as Promise<never>
  const clear = () => {
    if (ts) {
      clearTimeout(ts)
      ts = undefined
    }
  }

  return [timeoutPromise, clear]
}

// lodash replacements:

const toString = Object.prototype.toString

/**
 * Checks if `value` is a plain object, that is, an object created by the
 * `Object` constructor or one with a `[[Prototype]]` of `null`.
 *
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
 */
export const isObjectLike = (value: any): boolean => {
  return typeof value === 'object' && value !== null
}

/**
 * Checks if `value` is a plain object, that is, an object created by the
 * `Object` constructor or one with a `[[Prototype]]` of `null`.
 *
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
 */
export const isPlainObject = (value: any) => {
  if (!isObjectLike(value) || toString.call(value) !== '[object Object]') {
    return false
  }
  if (Object.getPrototypeOf(value) === null) {
    return true
  }
  let proto = value
  while (Object.getPrototypeOf(proto) !== null) {
    proto = Object.getPrototypeOf(proto)
  }
  return Object.getPrototypeOf(value) === proto
}

/**
 * Checks if `value` is classified as a boolean primitive or object.
 *
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a boolean, else `false`.
 */
export const isBoolean = (value: any): value is boolean => {
  return (
    value === true || value === false ||
    (isObjectLike(value) && toString.call(value) === '[object Boolean]')
  )
}

/**
 * Checks if `value` is classified as a `String` primitive or object.
 *
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a string, else `false`.
 */
export const isString = (value: any): value is string => {
  const type = typeof value
  return (
    type === 'string' ||
    (type === 'object' &&
      value != null &&
      !Array.isArray(value) &&
      toString.call(value) === '[object String]')
  )
}

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 */
export const isFunction = (value: any): value is (...args: any[]) => any => typeof value === 'function'

/**
 * Use Array.from and Set to dedup array or array like object.
 */
export const uniq = <T = any>(arr: Array<T>): Array<T> => (!arr || !arr.length ? [] : Array.from(new Set(Array.isArray(arr) ? arr : Array.from(arr))))

/**
 * Return a promise resolve in given ms.
 * Basically provide a promise version of setTimeout without return the timeout number.
 */
export const wait = (ms: number): Promise<void> => new Promise((r) => {
  setTimeout(r, ms)
})

export type ClassNamesArg = string | false | undefined | null
/**
 * Simplifies combining class names. Do not support Array or Object.
 * Copied from PanWDS.
 * @example
 * classNames('cls1 cls2', 'cls3',
 *   ifTruthy && 'truthy',
 *   ternaries ? 'yep' : 'nope',
 * );
 */
export const classNames = (...classes: ClassNamesArg[]): string => (
  classes.filter(Boolean).join(' ')
)

// ensure typings, avoid get types from @types/node
export const {
  setTimeout,
  clearTimeout,
  setInterval,
  clearInterval,
  requestIdleCallback,
  cancelIdleCallback,
} = window

/**
 * A no-operation function that returns undefined regardless of the arguments it receives.
 */
export function noop() { }

/**
 * A empty FC that returns null, render as nothing
 */
export const NullComponent: VFC<{}> = () => null
